import { Button, Collapse, Grid, IconButton, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { FieldArray, type FieldArrayRenderProps, useFormikContext } from 'formik';
import { Add, ArrowDown2, MessageQuestion, Trash } from 'iconsax-react';
import { type FC, type ReactElement, useState } from 'react';

import type { ProjectFormikType } from '@core/formik/project';
import type { QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import type { ProjectLossReason } from '@core/types/api/ProjectLossReason/ProjectLossReason';
import { getLossReasonOptions } from '@core/utils/competitors';
import { hasValue } from '@core/utils/equality';
import { FormikCurrency } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { hasFormikFieldsErrors } from '@web/utils';
import { lookups } from '@web/utils/DomainHelpers';

import { ExpandMore } from '../ExpandMore/ExpandMore';
import { Input } from '../Input/Input';
import { ApiLookupInput } from '../LookupInput/ApiLookupInput';
import { LocalLookupInput } from '../LookupInput/LocalLookupInput';
import type { LookupInputOption } from '../LookupInput/LookupInputSharedComponents';
import { SquaredBadge } from '../SquaredBadge/SquaredBadge';

type Props = {
  lossReasons: ProjectLossReason[];
  isLossReasonRequired?: boolean;
  isCompetitorRequired?: boolean;
  title: string;
  currencySymbol?: string;
};

export const LossReasonSection: FC<Props> = ({
  lossReasons,
  isLossReasonRequired = false,
  isCompetitorRequired = false,
  title,
  currencySymbol = '$',
}) => {
  const theme = useTheme();
  const formikBag = useFormikContext<ProjectFormikType | QuoteFormikType>();
  const [isOpen, setIsOpen] = useState(true);

  const hasError =
    formikBag.submitCount > 0 &&
    hasFormikFieldsErrors(formikBag, [
      'competitor',
      'lossReason',
      'lossReasonNotes',
      'competitorProducts',
    ]);
  const isCompetitorSelected = hasValue(formikBag.values.competitor?.id);

  const { data: competitorProducts } = useSlabQuery(
    'GET competitor products summaries by competitor ID',
    {
      pathParams: {
        id: formikBag.values.competitor?.id ?? '',
      },
    },
    {
      enabled: isCompetitorSelected,
    },
  );

  const selectedProducts = formikBag.values.competitorProducts ?? [];

  const allProductOptions: LookupInputOption[] = lookups({
    ...(competitorProducts ?? { items: [], count: 0 }),
    label: (p) => p.name,
  });

  const selectedIds = selectedProducts.map((cp) => cp.product.id).filter(hasValue);
  const getRowOptions = (rowIndex: number): LookupInputOption[] => {
    const currentId = selectedProducts[rowIndex]?.product.id ?? null;
    return allProductOptions.filter(
      (opt) => opt.value === currentId || !selectedIds.includes(opt.value),
    );
  };

  const allProductsExhausted =
    allProductOptions.length > 0 && selectedIds.length >= allProductOptions.length;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          backgroundColor: theme.palette.SlabCherry[100],
          border: `2px solid ${theme.palette.SlabCherry[600]}`,
          borderRadius: '8px',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExpandMore expand={isOpen} onClick={() => setIsOpen(!isOpen)}>
            <ArrowDown2 />
          </ExpandMore>
          <MessageQuestion size={24} color={theme.palette.SlabCherry[600]} />
          <Typography variant='h5/medium'>{title}</Typography>
        </Box>
        <Collapse in={isOpen} timeout='auto' unmountOnExit>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ApiLookupInput
                formState={formikBag.values}
                name='competitor'
                label={requiredLabel('Lost to (Competitor)', isCompetitorRequired)}
                onMatchChange={(): void => {
                  formikBag.setFieldValue('competitorProducts', []);
                }}
                route={{
                  barrel: 'GET competitors summaries',
                  args: (inputText, { constructQueryParams }) =>
                    constructQueryParams(inputText, {
                      filterBy: [
                        {
                          operation: Enums.FilterOperation.Equals,
                          name: 'isArchived',
                          values: [false],
                        },
                      ],
                    }),
                  transform: (competitorList): LookupInputOption[] =>
                    lookups({
                      ...competitorList,
                      label: (c) => c.name,
                    }),
                }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <LocalLookupInput
                formState={formikBag.values}
                name='lossReason'
                label={requiredLabel('Loss reason', isLossReasonRequired)}
                options={getLossReasonOptions(lossReasons, formikBag.values.lossReason?.option)}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Input
                name='lossReasonNotes'
                label='Notes'
                type='textarea'
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
              />
            </Grid>
          </Grid>
          {isCompetitorSelected && (
            <FieldArray name='competitorProducts'>
              {(arrayHelpers: FieldArrayRenderProps): ReactElement => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='h5/medium'>Products:</Typography>

                    {selectedProducts.length > 0 && (
                      <SquaredBadge badgeContent={selectedProducts.length} />
                    )}
                  </Box>

                  {selectedProducts.map((productSection, index) => (
                    <Box
                      key={productSection.product.id}
                      sx={{
                        backgroundColor: theme.palette.SlabGray[0],
                        borderRadius: '0.5rem',
                        border: `1px solid ${theme.palette.SlabGray.Stroke}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Typography variant='h6/medium' sx={{ flex: 1 }}>
                          Product {index + 1}
                        </Typography>
                        <IconButton onClick={(): void => arrayHelpers.remove(index)}>
                          <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2} sx={{ p: 2 }}>
                        <Grid item xs={6}>
                          <LocalLookupInput
                            formState={formikBag.values}
                            name={`competitorProducts.${index}.product`}
                            label='Product*'
                            options={getRowOptions(index)}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Input
                            type='currency'
                            name={`competitorProducts.${index}.price`}
                            label='Price'
                            startAdornment={currencySymbol}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type='button'
                      onClick={(): void =>
                        arrayHelpers.push({
                          product: { id: null, option: null },
                          price: FormikCurrency(undefined),
                        })
                      }
                      disabled={allProductsExhausted}
                      sx={{
                        textTransform: 'none',
                        color: theme.palette.SlabIndigo['700'],
                      }}
                      startIcon={<Add color={theme.palette.SlabIndigo['700']} />}
                    >
                      Add Existing Product
                    </Button>
                  </Box>
                </Box>
              )}
            </FieldArray>
          )}
        </Collapse>
      </Box>
      {!isOpen && hasError && (
        <Typography color='error'>Please address the errors above.</Typography>
      )}
    </Box>
  );
};
